/**
 * RuleForm - Form for creating/editing categorization rules (WP-CAT-12)
 *
 * Fields:
 * - Name (required)
 * - Pattern (required, regex)
 * - Pattern type (required)
 * - Target category (required)
 * - Priority (0-9999, default 100)
 * - Confidence (0.0-1.0, default 0.8)
 * - Active toggle
 */
import { useState, useCallback, useEffect } from 'react';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Select from '@/components/ui/Select';
import Alert from '@/components/ui/Alert';
import { useCategoryPreferences } from '@/hooks/useCategoryPreferences';
import { PATTERN_TYPE_CONFIG, type CategorizationRule, type RuleCreateRequest, type RuleUpdateRequest, type PatternType } from '@/types/categorizationRule';

// ============================================================================
// Props
// ============================================================================

export interface RuleFormProps {
  rule?: CategorizationRule | null;
  onSubmit: (data: RuleCreateRequest | RuleUpdateRequest) => void;
  onCancel: () => void;
  isSubmitting?: boolean;
}

// ============================================================================
// Types
// ============================================================================

interface FormData {
  name: string;
  pattern: string;
  patternType: PatternType;
  categoryId: string;
  priority: number;
  confidence: number;
  isActive: boolean;
}

interface FormErrors {
  name?: string;
  pattern?: string;
  patternType?: string;
  categoryId?: string;
  priority?: string;
  confidence?: string;
}

// ============================================================================
// Component
// ============================================================================

export default function RuleForm({
  rule,
  onSubmit,
  onCancel,
  isSubmitting = false,
}: RuleFormProps) {
  const isEditMode = !!rule;

  // Form state
  const [formData, setFormData] = useState<FormData>({
    name: '',
    pattern: '',
    patternType: 'description',
    categoryId: '',
    priority: 100,
    confidence: 0.8,
    isActive: true,
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [patternError, setPatternError] = useState<string | null>(null);

  // Get categories for selector
  const { data: categories } = useCategoryPreferences(true);

  // Populate form when editing
  useEffect(() => {
    if (rule) {
      setFormData({
        name: rule.name,
        pattern: rule.pattern,
        patternType: rule.patternType,
        categoryId: rule.categoryId,
        priority: rule.priority,
        confidence: rule.confidence,
        isActive: rule.isActive,
      });
    }
  }, [rule]);

  // Validate regex pattern
  const validatePattern = useCallback((pattern: string): boolean => {
    try {
      new RegExp(pattern);
      setPatternError(null);
      return true;
    } catch (e) {
      setPatternError(`Invalid regex: ${e instanceof Error ? e.message : 'Unknown error'}`);
      return false;
    }
  }, []);

  // Validate form
  const validateForm = useCallback((): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    } else if (formData.name.length > 100) {
      newErrors.name = 'Name must be 100 characters or less';
    }

    if (!formData.pattern.trim()) {
      newErrors.pattern = 'Pattern is required';
    } else if (!validatePattern(formData.pattern)) {
      newErrors.pattern = 'Invalid regex pattern';
    } else if (formData.pattern.length > 500) {
      newErrors.pattern = 'Pattern must be 500 characters or less';
    }

    if (!formData.categoryId) {
      newErrors.categoryId = 'Category is required';
    }

    if (formData.priority < 0 || formData.priority > 9999) {
      newErrors.priority = 'Priority must be between 0 and 9999';
    }

    if (formData.confidence < 0 || formData.confidence > 1) {
      newErrors.confidence = 'Confidence must be between 0 and 1';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, validatePattern]);

  // Handle field change
  const handleChange = useCallback(
    (field: keyof FormData, value: string | number | boolean) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
      // Clear error for this field
      if (errors[field as keyof FormErrors]) {
        setErrors((prev) => ({ ...prev, [field]: undefined }));
      }
      // Validate pattern on change
      if (field === 'pattern' && typeof value === 'string') {
        validatePattern(value);
      }
    },
    [errors, validatePattern]
  );

  // Handle form submit
  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();

      if (!validateForm()) return;

      if (isEditMode) {
        const updateData: RuleUpdateRequest = {
          name: formData.name,
          pattern: formData.pattern,
          patternType: formData.patternType,
          categoryId: formData.categoryId,
          priority: formData.priority,
          confidence: formData.confidence,
          isActive: formData.isActive,
        };
        onSubmit(updateData);
      } else {
        const createData: RuleCreateRequest = {
          name: formData.name,
          pattern: formData.pattern,
          patternType: formData.patternType,
          categoryId: formData.categoryId,
          priority: formData.priority,
          confidence: formData.confidence,
          isActive: formData.isActive,
        };
        onSubmit(createData);
      }
    },
    [validateForm, isEditMode, formData, onSubmit]
  );

  // Filter categories by active/enabled
  const categoryOptions = categories
    ?.filter((cat) => cat.isEnabled)
    .map((cat) => ({
      value: cat.id,
      label: `${cat.name} (${cat.type})`,
    })) || [];

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Name */}
      <div>
        <label htmlFor="rule-name" className="block text-sm font-medium text-gray-700 mb-1">
          Name <span className="text-red-500">*</span>
        </label>
        <Input
          id="rule-name"
          type="text"
          value={formData.name}
          onChange={(e) => handleChange('name', e.target.value)}
          placeholder="e.g., Grocery Stores, Coffee Shops"
          maxLength={100}
          error={errors.name}
        />
        {errors.name && (
          <p className="mt-1 text-sm text-red-600">{errors.name}</p>
        )}
      </div>

      {/* Pattern Type */}
      <div>
        <Select
          id="rule-pattern-type"
          label="Pattern Type *"
          value={formData.patternType}
          onChange={(e) => handleChange('patternType', e.target.value as PatternType)}
          options={Object.entries(PATTERN_TYPE_CONFIG).map(([key, config]) => ({
            value: key,
            label: `${config.label} - ${config.description}`,
          }))}
          hint="Which transaction field the pattern will match against."
        />
      </div>

      {/* Pattern */}
      <div>
        <label htmlFor="rule-pattern" className="block text-sm font-medium text-gray-700 mb-1">
          Pattern (Regex) <span className="text-red-500">*</span>
        </label>
        <Input
          id="rule-pattern"
          type="text"
          value={formData.pattern}
          onChange={(e) => handleChange('pattern', e.target.value)}
          placeholder="e.g., ^STARBUCKS.*, walmart|kroger|safeway"
          maxLength={500}
          error={errors.pattern || patternError || undefined}
          className="font-mono"
        />
        {(errors.pattern || patternError) && (
          <p className="mt-1 text-sm text-red-600">{errors.pattern || patternError}</p>
        )}
        <p className="mt-1 text-sm text-gray-500">
          Regular expression pattern. Case-insensitive matching.
        </p>
      </div>

      {/* Category */}
      <div>
        <Select
          id="rule-category"
          label="Target Category *"
          value={formData.categoryId}
          onChange={(e) => handleChange('categoryId', e.target.value)}
          placeholder="Select a category..."
          options={categoryOptions}
          error={errors.categoryId}
        />
      </div>

      {/* Priority and Confidence */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="rule-priority" className="block text-sm font-medium text-gray-700 mb-1">
            Priority
          </label>
          <Input
            id="rule-priority"
            type="number"
            value={formData.priority}
            onChange={(e) => handleChange('priority', parseInt(e.target.value) || 0)}
            min={0}
            max={9999}
            error={errors.priority}
          />
          <p className="mt-1 text-sm text-gray-500">
            Higher priority rules are evaluated first (0-9999).
          </p>
        </div>

        <div>
          <label htmlFor="rule-confidence" className="block text-sm font-medium text-gray-700 mb-1">
            Confidence
          </label>
          <Input
            id="rule-confidence"
            type="number"
            value={formData.confidence}
            onChange={(e) => handleChange('confidence', parseFloat(e.target.value) || 0)}
            min={0}
            max={1}
            step={0.1}
            error={errors.confidence}
          />
          <p className="mt-1 text-sm text-gray-500">
            Confidence score when rule matches (0.0-1.0).
          </p>
        </div>
      </div>

      {/* Active toggle */}
      <div className="flex items-center gap-3">
        <input
          id="rule-active"
          type="checkbox"
          checked={formData.isActive}
          onChange={(e) => handleChange('isActive', e.target.checked)}
          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
        />
        <label htmlFor="rule-active" className="text-sm font-medium text-gray-700">
          Active
        </label>
      </div>

      {/* Pattern test hint */}
      <Alert variant="info">
        <p className="text-sm">
          <strong>Tip:</strong> Use the "Test Rules" button in the Rules list to test your pattern against sample transactions before saving.
        </p>
      </Alert>

      {/* Actions */}
      <div className="flex gap-3 pt-4 border-t border-gray-200">
        <Button
          type="submit"
          variant="primary"
          isLoading={isSubmitting}
          disabled={isSubmitting}
        >
          {isEditMode ? 'Save Changes' : 'Create Rule'}
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={isSubmitting}
        >
          Cancel
        </Button>
      </div>
    </form>
  );
}
